import { useMemo } from 'react';

import { type UniversalCreateMenuSection } from '@atlassian/universal-create/types';
import { useIntl } from 'react-intl';

import { useUserHasCapabilities } from '@townsquare/capability-provider/useUserHasCapabilities';
import { filterUndefined } from '@townsquare/filter-type';
import { useGoalCapabilities } from '@townsquare/goal-capabilities';
import { useUniversalCreateItems as useBaseUniversalCreateItems } from '@townsquare/navigation/universal-create/items';
import { type UseUniversalCreateItemsProps } from '@townsquare/navigation/universal-create/types';
import { useProjectCapabilities } from '@townsquare/project-capabilities';
import { fg } from '@townsquare/stat-sig/gate';

export const useUniversalCreateItems = (): UseUniversalCreateItemsProps => {
	const intl = useIntl();
	const { teamCreate, goalCreate, projectCreate } = useBaseUniversalCreateItems();
	const userHasGoalsAccess = useUserHasCapabilities(['goals_app:access'], true);
	const userHasProjectsAccess = useUserHasCapabilities(['projects_app:access'], true);
	const projectCapabilities = useProjectCapabilities();
	const canCreateProject = fg('townsquare_m3_group_permissions')
		? projectCapabilities.canCreateProject
		: true;
	const { canCreateGoal } = useGoalCapabilities();
	const canCreateGoalItem = fg('townsquare_m3_group_permissions') ? canCreateGoal : true;

	const suggestionItems = useMemo(
		() => [teamCreate, userHasGoalsAccess && canCreateGoalItem ? goalCreate : undefined],
		[teamCreate, goalCreate, userHasGoalsAccess, canCreateGoalItem],
	);

	const filteredSuggestionItems = suggestionItems.filter(filterUndefined);

	const items = useMemo<UniversalCreateMenuSection[]>(() => {
		const sections: UniversalCreateMenuSection[] = [];

		if (userHasProjectsAccess && canCreateProject) {
			sections.push({
				type: 'appDefinedSection' as const,
				items: [projectCreate],
			});
		}

		if (filteredSuggestionItems.length > 0) {
			sections.push({
				type: 'appDefinedSection' as const,
				heading: intl.formatMessage({
					id: 'townsquare.projects.use-universal-create-items.section-title',
					description: 'Displays the other create options',
					defaultMessage: 'Suggestions',
				}),
				items: filteredSuggestionItems,
			});
		}

		return sections;
	}, [intl, projectCreate, userHasProjectsAccess, filteredSuggestionItems, canCreateProject]);

	return { items, compact: !!suggestionItems.filter(filterUndefined).length };
};
